/* W Network section kit v1.0. No dependencies; progressive enhancement only. */
(() => {
  'use strict';
  if (window.__WNHomeSectionsV1) return;
  window.__WNHomeSectionsV1 = true;
  const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const toggle = (node, visible) => { if (node) node.hidden = !visible; };

  class WNCarousel extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-wn-track]');
      if (!this.track) return;
      this.controls = this.querySelector('[data-wn-carousel-controls]');
      this.prev = this.querySelector('[data-wn-prev]');
      this.next = this.querySelector('[data-wn-next]');
      this.abort = new AbortController();
      const options = { signal: this.abort.signal };
      this.update = this.update.bind(this);
      this.track.addEventListener('scroll', this.update, { ...options, passive: true });
      this.prev?.addEventListener('click', () => this.move(-1), options);
      this.next?.addEventListener('click', () => this.move(1), options);
      if (this.track.hasAttribute('data-wn-drag')) this.enableMouseDrag(options);
      this.track.addEventListener('keydown', (event) => {
        if (event.target !== this.track || !this.overflow) return;
        const rtl = getComputedStyle(this.track).direction === 'rtl';
        const sign = rtl ? -1 : 1;
        if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
          event.preventDefault();
          this.move((event.key === 'ArrowRight' ? 1 : -1) * sign);
        } else if (event.key === 'Home' || event.key === 'End') {
          event.preventDefault();
          this.track.scrollTo({ left: event.key === 'Home' ? 0 : sign * this.track.scrollWidth, behavior: reducedMotion() ? 'auto' : 'smooth' });
        }
      }, options);
      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(this.update);
        this.resizeObserver.observe(this.track);
      } else {
        window.addEventListener('resize', this.update, options);
      }
      this.frame = requestAnimationFrame(this.update);
    }
    disconnectedCallback() {
      this.abort?.abort();
      this.resizeObserver?.disconnect();
      cancelAnimationFrame(this.frame);
    }
    update() {
      const max = Math.max(0, this.track.scrollWidth - this.track.clientWidth);
      this.overflow = max > 4 && getComputedStyle(this.track).overflowX !== 'visible';
      toggle(this.controls, this.overflow);
      if (this.overflow) this.track.tabIndex = 0;
      else this.track.removeAttribute('tabindex');
      const offset = Math.abs(this.track.scrollLeft);
      if (this.prev) this.prev.disabled = !this.overflow || offset <= 3;
      if (this.next) this.next.disabled = !this.overflow || offset >= max - 3;
    }
    move(direction) {
      const rtl = getComputedStyle(this.track).direction === 'rtl';
      const gap = parseFloat(getComputedStyle(this.track).columnGap) || 16;
      this.track.scrollBy({ left: direction * (rtl ? -1 : 1) * (this.track.clientWidth - gap), behavior: reducedMotion() ? 'auto' : 'smooth' });
    }
    enableMouseDrag(options) {
      this.track.addEventListener('dragstart', (event) => event.preventDefault(), options);
      this.track.addEventListener('click', (event) => {
        if (!this.blockClick) return;
        event.preventDefault();
        event.stopPropagation();
      }, { ...options, capture: true });
      this.track.addEventListener('pointerdown', (event) => {
        if (event.pointerType !== 'mouse' || event.button !== 0 || !this.overflow) return;
        this.dragPointer = event.pointerId;
        this.dragStartX = event.clientX;
        this.dragStartScroll = this.track.scrollLeft;
        this.dragged = false;
      }, options);
      this.track.addEventListener('pointermove', (event) => {
        if (event.pointerId !== this.dragPointer) return;
        const distance = event.clientX - this.dragStartX;
        if (!this.dragged && Math.abs(distance) > 5) {
          this.dragged = true;
          this.track.setPointerCapture(event.pointerId);
          this.track.classList.add('is-dragging');
        }
        if (!this.dragged) return;
        event.preventDefault();
        this.track.scrollLeft = this.dragStartScroll - distance;
      }, options);
      const finishDrag = (event) => {
        if (event.pointerId !== this.dragPointer) return;
        if (this.track.hasPointerCapture(event.pointerId)) this.track.releasePointerCapture(event.pointerId);
        this.dragPointer = null;
        this.track.classList.remove('is-dragging');
        if (this.dragged) {
          this.blockClick = true;
          window.setTimeout(() => { this.blockClick = false; }, 0);
        }
      };
      this.track.addEventListener('pointerup', finishDrag, options);
      this.track.addEventListener('pointercancel', finishDrag, options);
    }
  }

  // Explicit offsets prevent a Singapore deadline being interpreted as each visitor's local time.
  function parseDeadline(value) {
    if (!value) return null;
    const m = value.trim().match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2})(?:\.\d{1,3})?)?(Z|[+-]\d{2}:\d{2})$/);
    if (!m) return NaN;
    const [year, month, day, hour, minute, second] = m.slice(1, 7).map((x) => Number(x || 0));
    if (year < 2000 || month < 1 || month > 12 || day < 1 || day > new Date(Date.UTC(year, month, 0)).getUTCDate() || hour > 23 || minute > 59 || second > 59) return NaN;
    if (m[7] !== 'Z') {
      const [offsetHours, offsetMinutes] = m[7].slice(1).split(':').map(Number);
      if (offsetHours > 14 || offsetMinutes > 59 || (offsetHours === 14 && offsetMinutes !== 0)) return NaN;
    }
    return Date.parse(value.trim());
  }
  class WNSale extends HTMLElement {
    connectedCallback() {
      this.root = this.closest('.wn');
      this.scheduled = this.dataset.scheduled === 'true';
      this.start = parseDeadline(this.dataset.start);
      this.end = parseDeadline(this.dataset.end);
      this.editor = this.dataset.editor === 'true';
      this.clockOffset = 0;
      this.fields = ['days', 'hours', 'minutes', 'seconds'].map((unit) => this.querySelector(`[data-wn-${unit}]`));
      this.tick = this.tick.bind(this);
      this.abort = new AbortController();
      document.addEventListener('visibilitychange', this.tick, { signal: this.abort.signal });
      this.tick();
      if (this.scheduled && Number.isFinite(this.end)) this.interval = window.setInterval(this.tick, 1000);
    }
    disconnectedCallback() { clearInterval(this.interval); this.abort?.abort(); }
    tick() {
      const now = Date.now() + this.clockOffset;
      let state = 'active';
      let target = this.end;
      if (this.scheduled) {
        if (!Number.isFinite(this.end) || (this.dataset.start.trim() && !Number.isFinite(this.start)) || (this.start !== null && this.start >= this.end)) state = 'invalid';
        else if (now >= this.end) state = 'ended';
        else if (this.start !== null && now < this.start) { state = 'upcoming'; target = this.start; }
      }
      this.dataset.state = state;
      for (const name of ['active', 'upcoming', 'ended', 'invalid']) toggle(this.querySelector(`[data-wn-sale-${name}]`), state === name);
      const shouldHide = state === 'invalid' || (state === 'upcoming' && this.dataset.hideUpcoming === 'true') || (state === 'ended' && this.dataset.expiredAction === 'hide');
      if (this.root) this.root.hidden = !this.editor && shouldHide;
      const showTimer = this.scheduled && (state === 'active' || state === 'upcoming');
      toggle(this.querySelector('[data-wn-countdown]'), showTimer);
      if (!showTimer) return;
      const total = Math.max(0, Math.floor((target - now) / 1000));
      const values = [Math.floor(total / 86400), Math.floor(total % 86400 / 3600), Math.floor(total % 3600 / 60), total % 60];
      values.forEach((value, index) => {
        const text = String(value).padStart(2, '0');
        if (this.fields[index] && this.fields[index].textContent !== text) this.fields[index].textContent = text;
      });
    }
  }
  class WNFooterMenus extends HTMLElement {
    connectedCallback() {
      if (this.dataset.collapse !== 'true') return;
      this.media = window.matchMedia('(max-width: 749px)');
      this.apply = () => this.querySelectorAll('details.wn-footer-menu').forEach((menu) => { menu.open = !this.media.matches; });
      this.media.addEventListener('change', this.apply);
      this.apply();
    }
    disconnectedCallback() { this.media?.removeEventListener('change', this.apply); }
  }
  if (!customElements.get('wn-carousel')) customElements.define('wn-carousel', WNCarousel);
  if (!customElements.get('wn-sale')) customElements.define('wn-sale', WNSale);
  if (!customElements.get('wn-footer-menus')) customElements.define('wn-footer-menus', WNFooterMenus);

  // Native cart-page forms remain untouched. Only the explicit Ajax setting is intercepted.
  document.addEventListener('submit', async (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.matches('.wn-product-form[data-wn-product-form]') || form.dataset.mode !== 'ajax') return;
    event.preventDefault();
    if (form.dataset.busy === 'true') return;
    const button = form.querySelector('button[type="submit"]');
    const label = form.querySelector('[data-wn-button-label]');
    const status = form.querySelector('[data-wn-cart-status]');
    const confirmation = form.querySelector('[data-wn-cart-confirm]');
    const original = label?.textContent;
    form.dataset.busy = 'true';
    form.setAttribute('aria-busy', 'true');
    if (button) button.disabled = true;
    if (label) label.textContent = form.dataset.adding || 'Adding…';
    if (status) { status.textContent = ''; status.dataset.state = ''; }
    toggle(confirmation, false);
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), 20000);
    try {
      const endpoint = new URL(form.dataset.addUrl || form.action, window.location.origin);
      if (endpoint.origin !== window.location.origin) throw new Error('Invalid cart endpoint.');
      endpoint.pathname = `${endpoint.pathname.replace(/\.js$/, '')}.js`;
      const response = await fetch(endpoint, { method: 'POST', body: new FormData(form), headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' }, credentials: 'same-origin', signal: controller.signal });
      const data = await response.json();
      if (!response.ok || Number(data.status) >= 400) {
        const error = new Error(typeof data.description === 'string' ? data.description : 'The product could not be added.');
        error.confirmedFailure = true;
        throw error;
      }
      clearTimeout(timeout);
      if (status) { status.textContent = form.dataset.added || 'Added to your cart.'; status.dataset.state = 'success'; }
      toggle(confirmation, true);
      // Generic integration event; no assumptions about Dawn/Horizon/third-party drawer markup.
      document.dispatchEvent(new CustomEvent('wn:cart:updated', { detail: { added: data, cartUrl: form.dataset.cartUrl, source: form }, bubbles: true }));
    } catch (error) {
      if (status) {
        status.dataset.state = 'error';
        status.textContent = error.confirmedFailure ? error.message : (form.dataset.error || 'We could not confirm the result. Check your cart before trying again.');
      }
      // A network timeout can occur AFTER Shopify has accepted an add. Never automatically retry.
      toggle(confirmation, true);
    } finally {
      clearTimeout(timeout);
      form.dataset.busy = 'false';
      form.removeAttribute('aria-busy');
      if (button) button.disabled = false;
      if (label) label.textContent = original;
    }
  });

  // Optional browser-local saved list. No customer-account sync, fake hearts or stale saved prices.
  const storageKey = 'wn.saved.v1';
  let saved = null;
  let storageWorks = true;
  let dialog = null;
  let lastOpener = null;
  const safeProductUrl = (value) => {
    try { const url = new URL(value, location.origin); return url.origin === location.origin && ['http:', 'https:'].includes(url.protocol) ? url.pathname + url.search : ''; } catch { return ''; }
  };
  const safeImageUrl = (value) => {
    if (!value) return '';
    try {
      const url = new URL(value, location.origin);
      return ['http:', 'https:'].includes(url.protocol) && (url.origin === location.origin || url.hostname === 'cdn.shopify.com' || url.hostname.endsWith('.shopifycdn.com')) ? url.href : '';
    } catch { return ''; }
  };
  function sanitizeList(data) {
    if (!Array.isArray(data)) return [];
    const seen = new Set();
    return data.slice(0, 100).filter((item) => {
      if (!item || !/^\d{1,25}$/.test(String(item.id)) || typeof item.title !== 'string' || !safeProductUrl(item.url) || seen.has(String(item.id))) return false;
      seen.add(String(item.id)); return true;
    }).map((item) => ({ id: String(item.id), title: item.title.slice(0, 300), url: safeProductUrl(item.url), image: safeImageUrl(item.image) }));
  }
  function getSaved() {
    if (saved !== null) return saved;
    try { saved = sanitizeList(JSON.parse(localStorage.getItem(storageKey) || '[]')); }
    catch { saved = []; storageWorks = false; }
    return saved;
  }
  function persist() {
    try { localStorage.setItem(storageKey, JSON.stringify(getSaved())); }
    catch { storageWorks = false; }
    syncSavedButtons();
  }
  function syncSavedButtons(scope = document) {
    const items = getSaved();
    scope.querySelectorAll('[data-wn-save]').forEach((button) => {
      const active = items.some((item) => item.id === button.dataset.id);
      button.setAttribute('aria-pressed', String(active));
      button.setAttribute('aria-label', `${active ? button.dataset.removeLabel : button.dataset.saveLabel}: ${button.dataset.title}`);
    });
    document.querySelectorAll('[data-wn-saved-count]').forEach((counter) => { counter.textContent = `(${items.length})`; });
  }
  function element(tag, className, text) {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text !== undefined) node.textContent = text;
    return node;
  }
  function ensureDialog() {
    if (dialog) return;
    dialog = element('dialog', 'wn wn-saved-dialog');
    dialog.setAttribute('aria-labelledby', 'WNSavedHeading');
    const head = element('div', 'wn-saved-head');
    const heading = element('h2', '', 'Saved items');
    heading.id = 'WNSavedHeading'; heading.tabIndex = -1;
    const close = element('button', 'wn-icon-button', '×');
    close.type = 'button'; close.setAttribute('aria-label', 'Close saved items');
    close.addEventListener('click', () => dialog.close());
    head.append(heading, close);
    dialog.append(head, element('ul', 'wn-saved-list'), element('p', 'wn-saved-note'));
    dialog.querySelector('ul').setAttribute('role', 'list');
    dialog.addEventListener('close', () => { if (lastOpener?.isConnected) lastOpener.focus(); });
    dialog.addEventListener('click', (event) => {
      if (event.target !== dialog) return;
      const rect = dialog.getBoundingClientRect();
      if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) dialog.close();
    });
    document.body.append(dialog);
  }
  function renderSaved() {
    ensureDialog();
    const list = dialog.querySelector('.wn-saved-list');
    list.replaceChildren();
    const items = getSaved();
    if (!items.length) list.append(element('li', 'wn-saved-empty', 'No saved products yet. Use a heart button to save a product.'));
    items.forEach((item, index) => {
      const row = element('li', 'wn-saved-row');
      const link = element('a', 'wn-saved-product'); link.href = item.url;
      if (item.image) {
        const image = element('img'); image.src = item.image; image.alt = ''; image.width = 70; image.height = 70; image.loading = 'lazy'; link.append(image);
      }
      link.append(element('span', '', item.title));
      const remove = element('button', 'wn-icon-button', '×'); remove.type = 'button'; remove.setAttribute('aria-label', `Remove ${item.title}`);
      remove.addEventListener('click', () => {
        saved = getSaved().filter((entry) => entry.id !== item.id); persist(); renderSaved();
        const buttons = dialog.querySelectorAll('.wn-saved-row button');
        (buttons[Math.min(index, buttons.length - 1)] || dialog.querySelector('h2')).focus();
      });
      row.append(link, remove); list.append(row);
    });
    dialog.querySelector('.wn-saved-note').textContent = storageWorks ? 'Saved in this browser only, not your customer account. Open a product to check its current price and availability.' : 'Browser storage is unavailable. These saved items last only for this page session.';
  }
  document.addEventListener('click', (event) => {
    const target = event.target instanceof Element ? event.target : null;
    const save = target?.closest('[data-wn-save]');
    if (save) {
      const items = getSaved();
      const index = items.findIndex((item) => item.id === save.dataset.id);
      if (index >= 0) saved = items.filter((item) => item.id !== save.dataset.id);
      else {
        if (items.length >= 100) { lastOpener = save; renderSaved(); dialog.showModal(); dialog.querySelector('.wn-saved-note').textContent = 'Your saved list is full. Remove an item before saving another.'; return; }
        const item = sanitizeList([{ id: save.dataset.id, title: save.dataset.title, url: save.dataset.url, image: save.dataset.image }])[0];
        if (item) saved = [...items, item];
      }
      persist();
      return;
    }
    const open = target?.closest('[data-wn-open-saved]');
    if (open) { lastOpener = open; renderSaved(); dialog.showModal(); }
  });
  window.addEventListener('storage', (event) => {
    if (event.key !== storageKey) return;
    saved = null; syncSavedButtons();
    if (dialog?.open) renderSaved();
  });
  function enhance(scope = document) {
    scope.querySelectorAll('.wn [data-wn-js-only]').forEach((node) => { node.hidden = false; });
    if (scope.querySelector('[data-wn-save], [data-wn-open-saved]')) syncSavedButtons(scope);
  }
  document.addEventListener('shopify:section:load', (event) => enhance(event.target));
  document.addEventListener('shopify:block:select', (event) => {
    const target = event.target instanceof Element ? event.target : null;
    const item = target?.closest('.wn-track-item');
    if (item) item.scrollIntoView({ behavior: 'auto', block: 'nearest', inline: 'center' });
    const menu = target?.closest('details.wn-footer-menu');
    if (menu) menu.open = true;
  });
  enhance();
})();
